import React, { useEffect, useState } from "react";
import { useReducedMotion } from "framer-motion";
import { MapPin } from "lucide-react";

/*
 * HubCard — an immersive location card with a real city photo under a teal brand
 * overlay (keeps it on-brand + text readable), a pulsing coral "live" pin, mono
 * coordinates, the city's live local time, and a directions link. Falls back to
 * a brand gradient if the photo is missing. Designed to be used inside a sticky
 * stacking scroll.
 */
const MONO = "ui-monospace, SFMono-Regular, Menlo, monospace";

function useLocalTime(timeZone, reduced) {
  const fmt = (d) => {
    try {
      return new Intl.DateTimeFormat("en-GB", {
        timeZone,
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      }).format(d);
    } catch {
      return "--:--";
    }
  };
  const [time, setTime] = useState(() => fmt(new Date()));
  useEffect(() => {
    const tick = () => setTime(fmt(new Date()));
    tick();
    const id = window.setInterval(tick, reduced ? 60000 : 15000);
    return () => window.clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeZone, reduced]);
  return time;
}

export function HubCard({
  city,
  country,
  role,
  coords,
  timeZone,
  tzLabel,
  gradient,
  image,
  rotation = "",
  testId,
}) {
  const reduced = useReducedMotion();
  const time = useLocalTime(timeZone, reduced);
  const [imgOk, setImgOk] = useState(true);

  return (
    <div
      data-testid={testId}
      className={`group relative block w-full max-w-[940px] overflow-hidden rounded-[22px] shadow-2xl ${rotation}`}
      style={{ height: "min(74vh, 580px)", background: gradient }}
    >
      {/* real city photo */}
      {image && imgOk && (
        <img
          src={image}
          alt={`${city}, ${country}`}
          onError={() => setImgOk(false)}
          loading="lazy"
          draggable={false}
          className="absolute inset-0 h-full w-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-[1.06]"
        />
      )}
      {/* teal brand overlay — keeps photos on-brand and text legible */}
      <div
        aria-hidden="true"
        className="absolute inset-0"
        style={{ background: "linear-gradient(120deg, rgba(8,46,45,0.86) 0%, rgba(13,95,94,0.42) 45%, rgba(6,32,31,0.82) 100%)" }}
      />
      {/* faint terminal grid */}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,#F7F6F210_1px,transparent_1px),linear-gradient(to_bottom,#F7F6F210_1px,transparent_1px)] bg-[size:38px_38px]" />
      {/* soft coral glow */}
      <div
        className="pointer-events-none absolute -right-16 -top-16 h-56 w-56 rounded-full blur-3xl"
        style={{ background: "rgba(255,104,87,0.3)" }}
        aria-hidden="true"
      />

      {/* content */}
      <div className="relative z-10 flex h-full flex-col justify-between p-8 sm:p-11 text-white">
        <div className="flex items-start justify-between">
          <span className="inline-flex items-center gap-2 font-ui" style={{ fontSize: 11, letterSpacing: "0.18em", color: "rgba(247,246,242,0.9)" }}>
            <span className="relative inline-flex h-2 w-2">
              {!reduced && (
                <span className="absolute inline-flex h-full w-full rounded-full" style={{ background: "#FF6857", animation: "nxx-hub-ping 2s var(--nexxis-ease-soft) infinite" }} />
              )}
              <span className="relative inline-flex h-2 w-2 rounded-full" style={{ background: "#FF6857" }} />
            </span>
            {role}
          </span>
          <span className="inline-flex items-center gap-1.5 text-right" style={{ fontFamily: MONO, fontSize: 11.5, color: "rgba(247,246,242,0.78)" }}>
            <MapPin size={12} />
            {coords}
          </span>
        </div>

        <div>
          <div className="font-display" style={{ fontSize: "clamp(44px, 7vw, 84px)", lineHeight: 0.98, color: "#F7F6F2", fontWeight: 500, letterSpacing: "-0.02em" }}>
            {city}
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 font-ui" style={{ color: "rgba(247,246,242,0.85)", fontSize: 15 }}>
            <span>{country}</span>
            <span style={{ color: "rgba(247,246,242,0.4)" }}>·</span>
            <span style={{ fontFamily: MONO, fontSize: 13 }}>
              {time} <span style={{ color: "rgba(247,246,242,0.55)" }}>{tzLabel}</span>
            </span>
          </div>
          <div className="mt-6 h-px w-10" style={{ background: "#FF6857" }} />
        </div>
      </div>
    </div>
  );
}
